import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Form,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useKbList } from '../OpsKb/useOpsKb';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useMe } from '../../me/MeProvider';
import {
  useAttachmentDelete,
  useAttachmentUpload,
  useAttachmentUrl,
  useTicketAssign,
  useTicketDetail,
  useTicketReply,
  useTicketStatus,
} from './useTickets';

const STATUS_COLORS = {
  open: 'primary',
  pending: 'warning',
  resolved: 'success',
  closed: 'secondary',
};

const PRIORITY_COLORS = {
  low: 'secondary',
  normal: 'info',
  high: 'warning',
  urgent: 'danger',
};

/**
 * Status-Transition-Targets pro aktuellem Status.
 * Mirror der Status-Machine aus Spec/api-design.md.
 */
function nextStatuses(current, { canClose }) {
  switch (current) {
    case 'open':
      return ['pending', 'resolved'];
    case 'pending':
      return ['open', 'resolved'];
    case 'resolved':
      return canClose ? ['open', 'closed'] : ['open'];
    case 'closed':
      return ['open'];
    default:
      return [];
  }
}

const REPLY_KIND_LABELS = {
  customer_reply: { icon: '👤', label: 'Customer', tone: 'text-primary' },
  operator_reply: { icon: '💬', label: 'Operator', tone: 'text-success' },
  operator_note: { icon: '🔒', label: 'Note (intern)', tone: 'text-warning' },
};

export function TicketDetailDrawer({ ticketId, isOpen, onClose }) {
  const detail = useTicketDetail(ticketId);
  const ticket = detail.data?.ticket;
  const replies = detail.data?.replies ?? [];

  const canWrite = useHasPermission({ perms: ['tickets.write'] });
  const canAssign = useHasPermission({ perms: ['tickets.assign'] });
  const canClose = useHasPermission({ perms: ['tickets.close'] });
  const canNotes = useHasPermission({ perms: ['operatorNotes.write'] });

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={onClose}
      direction="end"
      style={{ width: 720 }}
    >
      <OffcanvasHeader toggle={onClose}>Ticket-Detail</OffcanvasHeader>
      <OffcanvasBody>
        {detail.isLoading && <p>Lade…</p>}
        {detail.isError && (
          <Alert color="danger">
            Ticket konnte nicht geladen werden: {detail.error?.message}
          </Alert>
        )}
        {ticket && (
          <>
            <TicketHeader
              ticket={ticket}
              canWrite={canWrite}
              canClose={canClose}
              canAssign={canAssign}
            />

            <RelatedRunbooks ticket={ticket} />

            <hr className="my-3" />

            <h6 className="text-uppercase small text-muted mb-2">Timeline</h6>
            <Timeline replies={replies} ticketId={ticket.id} canWrite={canWrite} />

            <hr className="my-3" />

            <HasPermission anyOf={['tickets.write', 'operatorNotes.write']}>
              <ReplyComposer
                ticketId={ticket.id}
                canWrite={canWrite}
                canNotes={canNotes}
              />
            </HasPermission>
          </>
        )}
      </OffcanvasBody>
    </Offcanvas>
  );
}

function TicketHeader({ ticket, canWrite, canClose, canAssign }) {
  const me = useMe().me;
  const statusMut = useTicketStatus();
  const assignMut = useTicketAssign();

  const transitions = nextStatuses(ticket.status, { canClose });

  const onSelfAssign = () => {
    if (!me?.user?.id) return;
    assignMut.mutate({ id: ticket.id, operatorUserId: me.user.id });
  };
  const onUnassign = () => {
    assignMut.mutate({ id: ticket.id, operatorUserId: null });
  };

  return (
    <>
      <h5 className="mb-1">
        <code className="me-2">{ticket.ticketNumber}</code>
        {ticket.subject}
      </h5>
      <div className="small text-muted mb-2">
        Customer: {ticket.customer?.displayName ?? ticket.customer?.email ?? '—'}
        {ticket.customer?.email && (
          <span className="ms-1">&lt;{ticket.customer.email}&gt;</span>
        )}
      </div>
      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
        <Badge color={STATUS_COLORS[ticket.status] ?? 'secondary'} pill>
          {ticket.status}
        </Badge>
        <Badge color={PRIORITY_COLORS[ticket.priority] ?? 'secondary'} pill>
          {ticket.priority}
        </Badge>
        {ticket.channel === 'webform' && (
          <Badge color="info" pill title="Vom Kunden im Portal erstellt">
            vom Kunden
          </Badge>
        )}
        {ticket.serviceId && (
          <Badge color="light" pill title="Zugeordnetes Produkt / Dienst">
            Dienst: {ticket.serviceId}
          </Badge>
        )}
        <span className="small text-muted">
          Assignee: {ticket.assignedTo?.displayName ?? (ticket.assignedTo?.id ? `#${ticket.assignedTo.id}` : '—')}
        </span>
      </div>

      <div className="d-flex flex-wrap gap-2">
        <HasPermission perms={['tickets.assign']}>
          {canAssign && (
            <ButtonGroup size="sm">
              {ticket.assignedTo?.id !== me?.user?.id && (
                <Button color="info" outline onClick={onSelfAssign} disabled={assignMut.isPending}>
                  Self-Assign
                </Button>
              )}
              {ticket.assignedTo && (
                <Button color="secondary" outline onClick={onUnassign} disabled={assignMut.isPending}>
                  Unassign
                </Button>
              )}
            </ButtonGroup>
          )}
        </HasPermission>

        <HasPermission perms={['tickets.write']}>
          {canWrite && transitions.length > 0 && (
            <div className="d-flex align-items-center gap-1">
              <span className="small text-muted">Status →</span>
              {transitions.map((s) => (
                <Button
                  key={s}
                  size="sm"
                  color={STATUS_COLORS[s] ?? 'secondary'}
                  outline
                  onClick={() => statusMut.mutate({ id: ticket.id, status: s })}
                  disabled={statusMut.isPending}
                >
                  {s}
                </Button>
              ))}
            </div>
          )}
        </HasPermission>
      </div>
    </>
  );
}

function RelatedRunbooks({ ticket }) {
  // Ops-KB nach Ticket-Betreff (+ Dienst als Zusatzsignal) durchsuchen und die
  // besten Treffer als Deep-Links anbieten — verbindet Tickets mit den Runbooks.
  const query = [ticket.subject, ticket.serviceId].filter(Boolean).join(' ').trim();
  const kb = useKbList({ q: query });
  const items = (kb.data?.items ?? []).slice(0, 5);
  if (!query || !items.length) return null;
  const brandSlug = (typeof window !== 'undefined' ? window.location.pathname.split('/')[1] : '') || '';
  return (
    <>
      <hr className="my-3" />
      <h6 className="text-uppercase small text-muted mb-2">Relevante Runbooks</h6>
      <ul className="list-unstyled mb-0">
        {items.map((a) => (
          <li key={a.id} className="mb-1 d-flex align-items-center gap-2">
            <Link to={`/${brandSlug}/ops-kb?article=${encodeURIComponent(a.slug)}`} className="small">
              📄 {a.title}
            </Link>
            {a.source === 'repo' && <Badge color="info" pill>repo</Badge>}
          </li>
        ))}
      </ul>
    </>
  );
}

function Timeline({ replies, ticketId, canWrite }) {
  if (replies.length === 0) {
    return <p className="text-muted small">Noch keine Replies.</p>;
  }
  return (
    <div className="d-flex flex-column gap-3">
      {replies.map((r) => (
        <ReplyBubble
          key={r.id}
          reply={r}
          ticketId={ticketId}
          canWrite={canWrite}
        />
      ))}
    </div>
  );
}

function ReplyBubble({ reply, ticketId, canWrite }) {
  const meta = REPLY_KIND_LABELS[reply.kind] ?? REPLY_KIND_LABELS.operator_reply;
  return (
    <div
      className="p-2 border rounded"
      style={reply.kind === 'operator_note' ? { background: 'rgba(255, 193, 7, 0.06)' } : {}}
    >
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className={`small fw-semibold ${meta.tone}`}>
          {meta.icon} {meta.label}
          {reply.authorCustomerEmail && (
            <span className="text-muted fw-normal ms-1">
              · {reply.authorCustomerEmail}
            </span>
          )}
        </span>
        <span className="small text-muted">
          {reply.createdAt && <RelativeTime iso={reply.createdAt} />}
        </span>
      </div>
      <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{reply.body}</div>
      {reply.attachments?.length > 0 && (
        <AttachmentList
          ticketId={ticketId}
          attachments={reply.attachments}
          canDelete={canWrite}
        />
      )}
    </div>
  );
}

function AttachmentList({ ticketId, attachments, canDelete }) {
  const getUrl = useAttachmentUrl();
  const del = useAttachmentDelete();

  const onDownload = async (attId) => {
    try {
      const res = await getUrl({ id: ticketId, attId });
      // LOW-1 (security-review 2026-06-21): nur https:-Presigned-URLs öffnen.
      // Defense-in-depth gegen einen falsch konfigurierten Storage-Backend
      // der z.B. javascript:-URLs durchreichen würde.
      if (typeof res?.url === 'string' && /^https:\/\//i.test(res.url)) {
        window.open(res.url, '_blank', 'noopener');
      }
    } catch (err) {
      // Toast wird vom Client geworfen — hier nichts zusätzlich tun
      // eslint-disable-next-line no-console
      console.error('attachment url failed', err);
    }
  };

  return (
    <div className="mt-2 d-flex flex-wrap gap-2">
      {attachments.map((a) => (
        <span
          key={a.id}
          className="small border rounded px-2 py-1 d-inline-flex align-items-center gap-2"
        >
          📎{' '}
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={() => onDownload(a.id)}
          >
            {a.filename}
          </button>
          <span className="text-muted">({formatBytes(a.size)})</span>
          {canDelete && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-danger"
              onClick={() => del.mutate({ id: ticketId, attId: a.id })}
              disabled={del.isPending}
              title="Attachment entfernen"
            >
              ✕
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

function ReplyComposer({ ticketId, canWrite, canNotes }) {
  const [body, setBody] = useState('');
  const [isNote, setIsNote] = useState(false);
  const [file, setFile] = useState(null);
  const reply = useTicketReply();
  const upload = useAttachmentUpload();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    const kind = isNote ? 'operator_note' : 'operator_reply';
    let attachmentIds = [];
    if (file) {
      try {
        const res = await upload.mutateAsync({ id: ticketId, file });
        if (res?.attachment?.id) attachmentIds = [res.attachment.id];
      } catch {
        return; // Toast vom Client
      }
    }
    await reply.mutateAsync({ id: ticketId, kind, body, attachmentIds });
    setBody('');
    setFile(null);
    setIsNote(false);
  };

  const allowReply = isNote ? canNotes : canWrite;

  return (
    <Form onSubmit={onSubmit}>
      <FormGroup>
        <Label className="small text-muted">
          {isNote ? 'Interne Notiz' : 'Reply an Customer'}
        </Label>
        <Input
          type="textarea"
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={isNote ? 'Nur intern sichtbar…' : 'Antwort an Customer…'}
          maxLength={50_000}
        />
      </FormGroup>
      <div className="d-flex flex-wrap align-items-center gap-3">
        <FormGroup check className="mb-0">
          <Input
            type="checkbox"
            id="ticket-is-note"
            checked={isNote}
            onChange={(e) => setIsNote(e.target.checked)}
            disabled={!canNotes}
          />
          <Label check for="ticket-is-note" className="small">
            Als interne Note (operator_note)
          </Label>
        </FormGroup>
        <FormGroup className="mb-0">
          <Input
            type="file"
            bsSize="sm"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </FormGroup>
        <Button
          color="primary"
          type="submit"
          disabled={!allowReply || !body.trim() || reply.isPending || upload.isPending}
          className="ms-auto"
        >
          {upload.isPending ? 'Lade Anhang…' : reply.isPending ? 'Sende…' : 'Senden'}
        </Button>
      </div>
    </Form>
  );
}

function formatBytes(n) {
  if (n == null) return '?';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
